/** Line-level diff for comparing prompt versions. No dependency — one screen of LCS. */

import { normalizeNewlines } from '@/lib/utils'

export type DiffLineType = 'equal' | 'add' | 'remove'

export interface DiffLine {
  type: DiffLineType
  text: string
}

/** Above this, the LCS matrix is not worth allocating; no seam caps prompt length. */
const MAX_DIFFED_LINES = 2000

/**
 * Classic LCS diff over lines. `add` means present in `next` only, `remove` in `prev` only.
 * Quadratic in line count, so oversized inputs degrade to a whole-text replacement.
 */
export function diffLines(prev: string, next: string): DiffLine[] {
  // Rows written before the write seams normalized can still hold `\r\n`; without this every
  // line differs by an invisible `\r` and a one-word edit renders as a full replacement.
  const a = normalizeNewlines(prev).split('\n')
  const b = normalizeNewlines(next).split('\n')

  if (a.length > MAX_DIFFED_LINES || b.length > MAX_DIFFED_LINES) {
    return [
      ...a.map((text): DiffLine => ({ type: 'remove', text })),
      ...b.map((text): DiffLine => ({ type: 'add', text })),
    ]
  }

  // lcs[i][j] = length of the longest common subsequence of a[i:] and b[j:].
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  )
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }

  const result: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      result.push({ type: 'equal', text: a[i] })
      i++
      j++
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      result.push({ type: 'remove', text: a[i] })
      i++
    } else {
      result.push({ type: 'add', text: b[j] })
      j++
    }
  }
  while (i < a.length) result.push({ type: 'remove', text: a[i++] })
  while (j < b.length) result.push({ type: 'add', text: b[j++] })

  return result
}
